import { useState, useEffect } from "react";
import { login as apiLogin, logout as apiLogout } from "../api/admin";

// Helper function to safely access localStorage (works in private browsing modes)
const safeLocalStorage = {
  getItem: (key: string): string | null => {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      console.warn("localStorage access error:", e);
      return null;
    }
  },
  setItem: (key: string, value: string): void => {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      console.warn("localStorage set error:", e);
    }
  },
  removeItem: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.warn("localStorage remove error:", e);
    }
  },
};

// Detect Safari browser
const isSafari = (): boolean => {
  return (
    /Safari/.test(navigator.userAgent) &&
    !/Chrome/.test(navigator.userAgent) &&
    !/Chromium/.test(navigator.userAgent)
  );
};

// Detect Firefox browser
const isFirefox = (): boolean => {
  return navigator.userAgent.toLowerCase().indexOf("firefox") > -1;
};

export const useAuth = () => {
  // Initialize with null to indicate "loading" state
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authStateChecked, setAuthStateChecked] = useState(false);

  // Check authentication status on component mount
  useEffect(() => {
    const checkAuth = async () => {
      setIsLoading(true);

      try {
        // Log browser detection to help with debugging
        console.log(
          "Browser detection - Safari:",
          isSafari(),
          "Firefox:",
          isFirefox()
        );

        const token = safeLocalStorage.getItem("authToken");

        if (token) {
          // Simple token validation - in a production app you might want to validate with the server
          try {
            // Just check if the token is a valid JWT format
            if (token.split(".").length === 3) {
              setIsAuthenticated(true);
            } else {
              safeLocalStorage.removeItem("authToken");
              setIsAuthenticated(false);
            }
          } catch (e) {
            // If token is invalid, remove it
            console.error("Invalid token:", e);
            safeLocalStorage.removeItem("authToken");
            setIsAuthenticated(false);
          }
        } else {
          setIsAuthenticated(false);
        }
      } catch (e) {
        console.error("Auth check error:", e);
        setIsAuthenticated(false);
      } finally {
        // Set auth state checked and loading to false
        setAuthStateChecked(true);
        setIsLoading(false);
      }
    };

    // Add a small delay for Firefox and Safari
    const timer = setTimeout(() => {
      checkAuth();
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const login = async (email: string, password: string) => {
    setError(null);
    setIsLoading(true);

    try {
      const token = await apiLogin(email, password);
      safeLocalStorage.setItem("authToken", token);

      // Add a small delay for Firefox and Safari
      setTimeout(() => {
        setIsAuthenticated(true);
        setIsLoading(false);
      }, 50);

      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
      setIsLoading(false);
      return false;
    }
  };

  const logout = () => {
    apiLogout();
    safeLocalStorage.removeItem("authToken");

    // Add a small delay for Firefox and Safari
    setTimeout(() => {
      setIsAuthenticated(false);
    }, 50);
  };

  return {
    isAuthenticated,
    isLoading,
    error,
    authStateChecked,
    login,
    logout,
  };
};